<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('exchanges.title') }}</h1>
          <div class="d-flex align-center">
            <v-text-field
              v-model="exchangeFilter"
              :label="$t('exchanges.filter')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              hide-details
              class="mr-3"
              style="max-width: 320px; min-width: 220px;"
              clearable
            />
            <v-btn color="primary" @click="openAddDialog">{{ $t('exchanges.add') }}</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('exchanges.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('exchanges.id') }}</th>
                  <th>{{ $t('exchanges.name') }}</th>
                  <th>{{ $t('exchanges.office') }}</th>
                  <th>{{ $t('exchanges.address') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="exchange in filteredExchanges" :key="exchange.id">
                  <td>{{ exchange.id }}</td>
                  <td><strong>{{ exchange.name }}</strong></td>
                  <td>
                    <v-chip v-if="exchange.office" color="primary" size="small">{{ $t('exchanges.physicalOffice') }}</v-chip>
                    <span v-else>-</span>
                  </td>
                  <td>{{ exchange.address || '-' }}</td>
                  <td>
                    <v-btn variant="plain" size="small" @click="editExchange(exchange)" icon="mdi-pencil"></v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="500" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ exchangeForm.id ? $t('exchanges.edit') : $t('exchanges.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="exchangeForm.name"
              :label="$t('exchanges.name')"
              :placeholder="$t('exchanges.namePlaceholder')"
              required
              :rules="[v => !!v || $t('exchanges.nameRequired')]"
            />
            <v-checkbox
              v-model="exchangeForm.office"
              :label="$t('exchanges.isPhysicalOffice')"
              class="mt-2"
            />
            <v-text-field
              v-model="exchangeForm.address"
              :label="$t('exchanges.address')"
              :placeholder="$t('exchanges.addressPlaceholder')"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveExchange">{{ exchangeForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut } from '../utils/api'

const { t } = useI18n()

interface Exchange {
  id: number
  name: string
  office?: boolean
  address?: string
}

const exchanges = ref<Exchange[]>([])
const exchangeFilter = ref('')
const showAddDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const exchangeForm = ref({
  id: null as number | null,
  name: '',
  office: false as boolean,
  address: '' as string
})

const loadExchanges = async () => {
  try {
    const response = await apiGet('/api/exchanges')
    if (response.ok) {
      exchanges.value = await response.json()
    }
  } catch (error) {
    console.error('Load exchanges error:', error)
  }
}

const filteredExchanges = computed(() => {
  if (!exchangeFilter.value) return exchanges.value
  const search = exchangeFilter.value.toLowerCase()
  return exchanges.value.filter(e => 
    e.name.toLowerCase().includes(search) ||
    (e.address && e.address.toLowerCase().includes(search))
  )
})

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editExchange = (exchange: Exchange) => {
  exchangeForm.value = {
    id: exchange.id,
    name: exchange.name,
    office: exchange.office || false,
    address: exchange.address || ''
  }
  showAddDialog.value = true
}

const resetForm = () => {
  exchangeForm.value = {
    id: null,
    name: '',
    office: false,
    address: ''
  }
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}

const saveExchange = async () => {
  if (!exchangeForm.value.name) {
    showError(t('exchanges.fillName'))
    return
  }

  try {
    let response
    if (exchangeForm.value.id) {
      // Update existing exchange
      response = await apiPut(`/api/exchanges/${exchangeForm.value.id}`, { exchange: exchangeForm.value })
    } else {
      // Create new exchange
      response = await apiPost('/api/exchanges', { exchange: exchangeForm.value })
    }

    if (response.ok) {
      await loadExchanges()
      showAddDialog.value = false
      resetForm()
    } else {
      const errorData = await response.json()
      const errorText = errorData.errors?.join(', ') || t('errors.unknownError')
      showError(t('exchanges.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save exchange error:', error)
    const errorText = error instanceof Error ? error.message : t('exchanges.errorSaving', { errors: error.message })
    showError(t('exchanges.errorSaving', { errors: errorText }))
  }
}

onMounted(() => {
  loadExchanges()
})
</script>
